<template>
    <div>
        <div class="text-center mb-6">
            <v-icon size="48" color="primary" class="mb-2">mdi-shape-outline</v-icon>
            <h3 class="text-h6 font-weight-bold">Set Up Your Tracker</h3>
            <p class="text-body-2 text-grey-darken-1">Choose what you're tracking and optionally add a photo</p>
        </div>

        <v-alert v-if="errors.length" class="mb-4" type="error" density="compact" variant="tonal" closable>
            <div v-for="error in errors" :key="error">{{ error }}</div>
        </v-alert>

        <!-- Asset Type Selection -->
        <p class="text-body-2 font-weight-medium mb-3">What are you tracking?</p>
        <div v-if="loadingTypes" class="d-flex justify-center py-6">
            <v-progress-circular indeterminate color="primary"/>
        </div>
        <div v-else class="asset-type-grid mb-4">
            <v-card
                v-for="type in assetTypes"
                :key="type.id"
                :class="{'asset-type-selected': selectedType === type.id}"
                class="asset-type-card text-center pa-4 cursor-pointer"
                variant="outlined"
                rounded="lg"
                @click="selectedType = type.id"
            >
                <v-img
                    v-if="type.image"
                    :src="type.image"
                    height="48"
                    class="mb-2 mx-auto"
                    contain
                />
                <v-icon v-else size="36" color="primary" class="mb-2">mdi-help-circle-outline</v-icon>
                <p class="text-body-2 font-weight-medium mb-0">{{ type.name }}</p>
            </v-card>
        </div>

        <!-- Optional Photo Upload -->
        <div class="mb-4">
            <p class="text-body-2 font-weight-medium mb-2">Add a photo (optional)</p>
            <div class="d-flex align-center ga-3">
                <v-avatar v-if="imagePreview" size="80" rounded="lg">
                    <v-img :src="imagePreview"/>
                </v-avatar>
                <div>
                    <v-btn variant="tonal" size="small" prepend-icon="mdi-camera" @click="$refs.fileInput.click()">
                        {{ imagePreview ? 'Change Photo' : 'Upload Photo' }}
                    </v-btn>
                    <v-btn v-if="imagePreview" variant="text" size="small" color="error" class="ml-1" @click="clearImage">
                        Remove
                    </v-btn>
                    <input ref="fileInput" type="file" accept="image/*" class="d-none" @change="onFileSelected"/>
                </div>
            </div>
        </div>

        <v-btn
            color="primary"
            size="large"
            block
            :disabled="!selectedType || processing"
            :loading="processing"
            @click="submit"
        >
            <v-icon start>mdi-check</v-icon>
            Complete Setup
        </v-btn>
    </div>
</template>

<script setup>
import {ref, onMounted, onBeforeUnmount} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const props = defineProps({
    deviceId: {type: [Number, String], required: true},
    nickname: {type: String, default: ''},
});

const emit = defineEmits(['completed']);
const snackbar = useSnackbarStore();

const assetTypes = ref([]);
const selectedType = ref(null);
const loadingTypes = ref(true);
const processing = ref(false);
const errors = ref([]);
const imageFile = ref(null);
const imagePreview = ref(null);

onMounted(() => {
    fetchAssetTypes();
});

const fetchAssetTypes = () => {
    loadingTypes.value = true;
    send('get', 'asset-types', {list: true})
        .then((response) => {
            assetTypes.value = response.data.data;
            if (assetTypes.value.length === 1) {
                selectedType.value = assetTypes.value[0].id;
            }
        })
        .catch(() => {
            snackbar.showSnackbar('Could not load tracking types', 'error');
        })
        .finally(() => {
            loadingTypes.value = false;
        });
};

const onFileSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
        snackbar.showSnackbar('Please select a valid image file', 'error');
        return;
    }
    if (file.size > 2 * 1024 * 1024) {
        snackbar.showSnackbar('Image must be under 2MB', 'error');
        return;
    }
    if (imagePreview.value) URL.revokeObjectURL(imagePreview.value);
    imageFile.value = file;
    imagePreview.value = URL.createObjectURL(file);
};

const clearImage = () => {
    if (imagePreview.value) URL.revokeObjectURL(imagePreview.value);
    imageFile.value = null;
    imagePreview.value = null;
};

onBeforeUnmount(() => {
    if (imagePreview.value) URL.revokeObjectURL(imagePreview.value);
});

const submit = () => {
    errors.value = [];
    processing.value = true;

    const formData = new FormData();
    formData.append('_method', 'put');
    formData.append('asset_type', selectedType.value);
    formData.append('label', props.nickname);
    if (imageFile.value) {
        formData.append('file', imageFile.value);
    }

    send('post', `personal-trackers/update/${props.deviceId}`, formData, {
        headers: {'Content-Type': 'multipart/form-data'},
    })
        .then((response) => {
            snackbar.showSnackbar('Tracker set up successfully!');
            emit('completed');
        })
        .catch((errorResponse) => {
            if (errorResponse.response?.data?.errors) {
                const validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response?.data?.message) {
                errors.value.push(errorResponse.response.data.message);
            } else {
                errors.value.push('Could not complete setup. Please try again.');
            }
            snackbar.showSnackbar('Setup failed', 'error');
        })
        .finally(() => {
            processing.value = false;
        });
};
</script>

<style scoped>
.asset-type-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
}

.asset-type-card {
    transition: all 0.2s ease;
    border: 2px solid transparent !important;
}

.asset-type-card:hover {
    border-color: rgba(var(--v-theme-primary), 0.3) !important;
    transform: translateY(-2px);
}

.asset-type-selected {
    border-color: rgb(var(--v-theme-primary)) !important;
    background: rgba(var(--v-theme-primary), 0.05);
}
</style>
